import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/**
 * Who is contributing - only asked of visitors who are not signed in
 * (signed-in contributors are known from their account). Consent to the terms
 * is given when submitting.
 */
export default function UserMetadataForm({ onNext, initialValues, isRestricted }) {
    const [userMetadata, setUserMetadata] = useState({ name: "", email: "", orcid: "" });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (initialValues) {
            setUserMetadata(prev => ({ ...prev, ...initialValues }));
        }
    }, [initialValues]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let finalValue = value;
        // ORCID as 0000-0000-0000-0000
        if (name === "orcid" && !isRestricted) {
            const digits = value.replace(/[^\dX]/gi, '').toUpperCase();
            finalValue = digits.slice(0, 16).replace(/(.{4})(?=.)/g, '$1-');
        }
        setUserMetadata(prev => ({ ...prev, [name]: finalValue }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const next = {};
        if (!userMetadata.name.trim()) next.name = "Please enter your name.";
        if (!isValidEmail(userMetadata.email || "")) next.email = "Please enter a valid email address.";
        if (userMetadata.orcid && !/^\d{4}-\d{4}-\d{4}-\d{3}[\dX]$/.test(userMetadata.orcid)) next.orcid = "An ORCID iD has 16 characters, in four groups of four, e.g. 0000-0000-0000-0000.";
        setErrors(next);
        if (Object.keys(next).length === 0) onNext(userMetadata);
    };

    return (
        <form onSubmit={handleSubmit} className="cx-card" noValidate>
            <div className="cx-card__head">
                <div>
                    <h2>About you</h2>
                    <p>Used to credit you in the dataset and to contact you about this contribution.</p>
                </div>
            </div>
            <div className="cx-card__body">
                <div className="cx-notice cx-notice--info">
                    <span><i className="bi bi-person-check me-2"></i>Have an account? <Link to="/login">Sign in</Link> to skip this step and follow your contributions in My Submissions.</span>
                </div>
                <div className="cx-grid">
                    <div className="cx-field">
                        <label htmlFor="name">Full name<span className="cx-req">*</span></label>
                        <input id="name" name="name" className="cx-input" value={userMetadata.name} onChange={handleChange}
                            autoComplete="name" disabled={isRestricted} aria-invalid={!!errors.name} aria-describedby={errors.name ? "name-error" : undefined} />
                        {errors.name && <div id="name-error" className="cx-help cx-help--error">{errors.name}</div>}
                    </div>
                    <div className="cx-field">
                        <label htmlFor="email">Email<span className="cx-req">*</span></label>
                        <input id="email" name="email" type="email" className="cx-input" value={userMetadata.email} onChange={handleChange}
                            autoComplete="email" placeholder="name@institution.edu" disabled={isRestricted}
                            aria-invalid={!!errors.email} aria-describedby={errors.email ? "email-error" : undefined} />
                        {errors.email && <div id="email-error" className="cx-help cx-help--error">{errors.email}</div>}
                    </div>
                    <div className="cx-field">
                        <label htmlFor="orcid">ORCID iD <span className="cx-optional">optional</span></label>
                        <input id="orcid" name="orcid" className="cx-input" value={userMetadata.orcid} onChange={handleChange}
                            placeholder="0000-0000-0000-0000" inputMode="numeric" disabled={isRestricted}
                            aria-invalid={!!errors.orcid} aria-describedby="orcid-help" />
                        <div id="orcid-help" className={`cx-help ${errors.orcid ? 'cx-help--error' : ''}`}>
                            {errors.orcid || "Links the contribution to your researcher profile."}
                        </div>
                    </div>
                </div>
            </div>
            <div className="cx-card__foot">
                <span className="cx-help m-0">Your email is never shown publicly.</span>
                <button type="submit" className="cx-btn cx-btn--primary">
                    Continue <i className="bi bi-arrow-right" aria-hidden="true"></i>
                </button>
            </div>
        </form>
    );
}
